import {
  Avatar,
  Badge,
  Box,
  FlexBox,
  IconButton,
  Table,
  Text,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { ComponentProps, ReactNode, useCallback, useMemo } from 'react';
import { useToggle, useUpdateEffect } from 'react-use';

import { Contact } from '../../mergeContactsModal.types';

import styles from './compareContactsTable.module.scss';

export type CompareContactsTableProps = {
  contacts: Contact[];
  previewContact: Contact;
} & ComponentProps<typeof Table>;

type CompareContactsNormalizedContact = {
  given_name: Contact['given_name'];
  family_name: Contact['family_name'];
  name: Contact['name'];
  email: Contact['email'];
  additional_emails?: Contact['additional_emails'];
  phone_number: Contact['phone_number'];
  additional_phone_numbers?: Contact['additional_phone_numbers'];
  avatar_url: Contact['avatar_url'];
  company?: string;
};

const primaryContactPointProperties: (keyof CompareContactsNormalizedContact)[] =
  ['email', 'phone_number'];

export const CompareContactsTable = ({
  contacts = [],
  previewContact,
  ...rest
}: CompareContactsTableProps): JSX.Element => {
  const normalizedContacts = useMemo((): CompareContactsNormalizedContact[] => {
    return contacts.map((contact) => ({
      ...contact,
      company: contact.company?.name,
    }));
  }, [contacts]);

  const normalizedPreviewContact =
    useMemo((): CompareContactsNormalizedContact => {
      return {
        ...previewContact,
        company: previewContact.company?.name,
      };
    }, [previewContact]);

  const renderPropertyValue = useCallback(
    (
      value: string | null | undefined,
      { isDisabled, isPrimary }: { isDisabled?: boolean; isPrimary?: boolean },
    ): ReactNode => {
      return (
        <FlexBox alignItems="center" gap="200">
          <Text.Paragraph as="span">
            {isDisabled ? (
              <s className={styles.strikethrough}>{value}</s>
            ) : (
              value
            )}
          </Text.Paragraph>

          {isPrimary && (
            <Badge variant="primary" size="small">
              Primary
            </Badge>
          )}
        </FlexBox>
      );
    },
    [],
  );

  const renderProperty = useCallback(
    (property: keyof CompareContactsNormalizedContact): ReactNode => {
      const previewValue = normalizedPreviewContact[property];

      return normalizedContacts.map((contact, i) => {
        const value = contact[property];

        return (
          <Table.Td key={`table-td-${property}-${i}`}>
            {Array.isArray(value) ? (
              <FlexBox
                as="ul"
                flexDirection="column"
                gap="500"
                className={styles.tableList}
              >
                {value.map((val) => (
                  <li key={val}>{renderPropertyValue(val, {})}</li>
                ))}
              </FlexBox>
            ) : (
              renderPropertyValue(value, {
                isDisabled:
                  !primaryContactPointProperties.includes(property) &&
                  previewValue !== value,
                isPrimary: primaryContactPointProperties.some(
                  (p) => p === property && previewValue === value,
                ),
              })
            )}
          </Table.Td>
        );
      });
    },
    [normalizedContacts, normalizedPreviewContact, renderPropertyValue],
  );

  const renderAvatarProperty = useCallback(() => {
    return normalizedContacts.map((contact, i) => {
      return (
        <Table.Td key={`table-td-${'avatar'}-${i}`}>
          {!!contact.avatar_url && (
            <Box
              className={classnames(styles.avatarContainer, {
                [styles.isForRemoval]:
                  normalizedPreviewContact.avatar_url !== contact.avatar_url,
              })}
            >
              <Avatar
                size="24"
                user={{
                  avatar_url: contact.avatar_url,
                }}
                className={styles.avatar}
              />
            </Box>
          )}
        </Table.Td>
      );
    });
  }, [normalizedContacts, normalizedPreviewContact]);

  const hasProperty = useCallback(
    (property: keyof CompareContactsNormalizedContact) => {
      return normalizedContacts.some((contact) => !isEmpty(contact[property]));
    },
    [normalizedContacts],
  );

  const [showAdditionalEmails, toggleShowAdditionalEmails] = useToggle(false);
  const [showAdditionalPhones, toggleShowAdditionalPhones] = useToggle(false);

  useUpdateEffect(() => {
    if (showAdditionalPhones) {
      toggleShowAdditionalEmails(false);
    }
  }, [showAdditionalPhones]);

  useUpdateEffect(() => {
    if (showAdditionalEmails) {
      toggleShowAdditionalPhones(false);
    }
  }, [showAdditionalEmails]);

  const totals = useMemo(
    () =>
      normalizedContacts.reduce(
        (acc, contact) => {
          acc.emails =
            acc.emails +
            (contact.email ? 1 : 0) +
            (contact.additional_emails ?? [])?.length;

          acc.phones =
            acc.phones +
            (contact.phone_number ? 1 : 0) +
            (contact.additional_phone_numbers ?? [])?.length;

          return acc;
        },
        { emails: 0, phones: 0 },
      ),
    [normalizedContacts],
  );

  return (
    <Table
      {...rest}
      className={classnames(styles.compareTable, rest.className)}
    >
      <Table.Thead>
        <Table.Tr>
          <Table.Th scope="rowgroup" width="148px">
            Information
          </Table.Th>

          {contacts.map((_, i) => (
            <Table.Th
              className={i < contacts.length ? styles.th : ''}
              key={`table-head-th-${i}`}
              width="100%"
            >
              Contact {i + 1}
            </Table.Th>
          ))}
        </Table.Tr>
      </Table.Thead>

      <Table.Tbody>
        {hasProperty('avatar_url') && (
          <Table.Tr>
            <Table.Th scope="rowgroup">Avatar</Table.Th>
            {renderAvatarProperty()}
          </Table.Tr>
        )}

        {hasProperty('given_name') && (
          <Table.Tr>
            <Table.Th scope="rowgroup">First name</Table.Th>
            {renderProperty('given_name')}
          </Table.Tr>
        )}

        {hasProperty('family_name') && (
          <Table.Tr>
            <Table.Th scope="rowgroup">Last name</Table.Th>
            {renderProperty('family_name')}
          </Table.Tr>
        )}

        {hasProperty('name') && (
          <Table.Tr>
            <Table.Th scope="rowgroup">Display name</Table.Th>
            {renderProperty('name')}
          </Table.Tr>
        )}

        {hasProperty('email') && (
          <Table.Tr
            className={classnames({
              [styles.borderless]: showAdditionalEmails,
            })}
          >
            <Table.Th scope="rowgroup" rowSpan={showAdditionalEmails ? 2 : 1}>
              <FlexBox alignItems="center" gap="200">
                <Text.Paragraph>Email</Text.Paragraph>

                {hasProperty('additional_emails') && (
                  <>
                    <Badge variant="primary" size="small">
                      {totals.emails}
                    </Badge>

                    <IconButton
                      className={styles.toggleButton}
                      variant="tertiary"
                      icon={showAdditionalEmails ? 'angle-up' : 'angle-down'}
                      onClick={toggleShowAdditionalEmails}
                    />
                  </>
                )}
              </FlexBox>
            </Table.Th>

            {renderProperty('email')}
          </Table.Tr>
        )}
        {showAdditionalEmails && (
          <Table.Tr>{renderProperty('additional_emails')}</Table.Tr>
        )}

        {hasProperty('phone_number') && (
          <Table.Tr
            className={classnames({
              [styles.borderless]: showAdditionalPhones,
            })}
          >
            <Table.Th scope="rowgroup" rowSpan={showAdditionalPhones ? 2 : 1}>
              <FlexBox alignItems="center" gap="200">
                <Text.Paragraph>Phone</Text.Paragraph>

                {hasProperty('additional_phone_numbers') && (
                  <>
                    <Badge variant="primary" size="small">
                      {totals.phones}
                    </Badge>

                    <IconButton
                      className={styles.toggleButton}
                      variant="tertiary"
                      icon={showAdditionalPhones ? 'angle-up' : 'angle-down'}
                      onClick={toggleShowAdditionalPhones}
                    />
                  </>
                )}
              </FlexBox>
            </Table.Th>

            {renderProperty('phone_number')}
          </Table.Tr>
        )}
        {showAdditionalPhones && (
          <Table.Tr>{renderProperty('additional_phone_numbers')}</Table.Tr>
        )}

        {hasProperty('company') && (
          <Table.Tr>
            <Table.Th scope="rowgroup">Company</Table.Th>
            {renderProperty('company')}
          </Table.Tr>
        )}
      </Table.Tbody>
    </Table>
  );
};
